// Partner content model.
//
// Fields are ported from the original static site's "Register New Partner"
// form (admin.html) plus the public partner-register.html form (position/title,
// partner address).
//
// This is the *company* record. The people who sign in on its behalf are
// separate docs — see types_partnerUser.ts — created by redeeming an invitation
// (types_invite.ts). No credentials are ever stored here; Firebase Auth holds
// them.

import type { HistoryInfoType } from './types_history';

// Firestore collection name.
export const coll_partners = 'partners';

// Archived edit-history steps, written by the `partners_written` Cloud
// Function. The name is derived server-side as `{collection} + 'history'`, so
// the two must be kept in step. Readable by Eighty Days admins only — partners
// do not see the history of their own record.
export const coll_partnersHistory = coll_partners + 'history';

export interface PartnerType {
  id: string;

  /**
   * The two characters that stand for this partner inside a tour request code,
   * unique across the partner list, or '' for a partner that has not been given
   * one yet (every partner predating the field).
   *
   * Eighty Days' to set, never the partner's: it is absent from
   * `partnerEditableKeys()` in the rules, so a partner owner's write is refused,
   * and absent from `PartnerProfileDraftType`, so their profile page has no
   * field for it. They can read it — it sits on the record they read — which is
   * why nothing about it is treated as a secret.
   *
   * Not the document id. The id is the slug the record is addressed by; a code
   * is a label Eighty Days chooses, and re-cutting one leaves existing request
   * codes pointing at the old two characters.
   */
  code: string;

  // Partner.
  name: string;
  brand: string;
  website: string;
  address: string;

  // Primary contact.
  contactName: string;
  position: string;
  contactEmail: string;
  phone: string;

  // Accounting.
  accountingName: string;
  accountingEmail: string;
  billingAddress: string;

  // Acceptance of the NDA and terms of service, given by the partner's manager
  // during onboarding (see `PartnerOnboardingGate`). Absent means "not yet
  // accepted", which is one half of what blocks a manager from the site.
  //
  // `termsVersion` records *which* wording was accepted, so a future revision is
  // distinguishable from the original. Bumping PARTNER_TERMS_VERSION does not
  // currently force re-acceptance — `partnerOnboardingComplete` only looks for a
  // timestamp — but it is the one line that would.
  termsAcceptedAt?: string;
  termsAcceptedByUid?: string;
  termsVersion?: number;

  // The partner's single manager account (`partnerUsers/{uid}` with role 'owner').
  // A partner has at most one, and this field is what enforces that: redemption
  // claims it inside a transaction, so two invitations racing to create an owner
  // cannot both win. Written only by Cloud Functions; cleared when the owner's
  // account is deleted, which is how ownership is handed over.
  ownerUid?: string;

  createdAt?: string;
  updatedAt?: string;

  // Persisted edit history, populated by autosave. Absent until the first edit.
  history?: HistoryInfoType;
}

// A blank partner for the "new partner" admin form.
/**
 * A stored partner, with anything added to the type since it was written filled
 * in from the defaults. The counterpart of `normaliseExperience` — see there for
 * why a cast is not enough, and why defaulting here writes nothing back.
 */
export function normalisePartner(data: Partial<PartnerType>, id: string): PartnerType {
  return { ...makeEmptyPartner(), ...data, id };
}

export function makeEmptyPartner(): PartnerType {
  return {
    id: '',
    code: '',
    name: '',
    brand: '',
    website: '',
    address: '',
    contactName: '',
    position: '',
    contactEmail: '',
    phone: '',
    accountingName: '',
    accountingEmail: '',
    billingAddress: '',
  };
}

// --- Onboarding ---
//
// A partner's manager cannot use the site until the partner record is filled in
// and the terms are accepted. The wording of that gate lives here rather than in
// the components, because two places have to agree on it: the registration
// wizard on the invitation page, which collects the answers, and
// `PartnerOnboardingGate`, which decides whether to keep asking.

/** Bump when the NDA / terms wording changes. See `PartnerType.termsVersion`. */
export const PARTNER_TERMS_VERSION = 1;

/**
 * The subset of a partner record its own manager fills in — exactly the keys the
 * security rules let a partner owner write, minus the two Eighty Days owns
 * (`name`, `brand`). Same set the profile page edits, gathered into one object
 * because onboarding submits it in a single write.
 */
export type PartnerProfileDraftType = Pick<
  PartnerType,
  | 'website'
  | 'address'
  | 'contactName'
  | 'position'
  | 'contactEmail'
  | 'phone'
  | 'accountingName'
  | 'accountingEmail'
  | 'billingAddress'
>;

/**
 * What "all the information" means. A manager is held at step 1 until every one
 * of these is non-blank.
 *
 * `website` is deliberately absent: it is not something a concierge necessarily
 * has to hand on their first sign-in, and locking a partner out of the site over
 * an optional URL would be a poor trade. Moving it into this list is all it
 * takes to make it mandatory.
 */
export const partnerRequiredProfileFields = [
  'address',
  'contactName',
  'position',
  'contactEmail',
  'phone',
  'accountingName',
  'accountingEmail',
  'billingAddress',
] as const satisfies readonly (keyof PartnerProfileDraftType)[];

// --- Invitation prefill ---
//
// What a manager's registration wizard is told about their partner *before* they
// have an account to read it with. Returned by `invitePreview`, so the audience
// is whoever holds the invitation link — which is why the set is a whitelist,
// built by naming every field, and not a subtraction from PartnerType. Adding a
// field to the partner record must never widen this by accident.

/**
 * Fields the wizard may prefill into its form. Partner and primary contact only.
 *
 * Accounting is deliberately absent. A billing address and the accounts contact
 * are the one part of a partner record that is genuinely theirs to keep, and an
 * invitation link can be forwarded, sit in a shared inbox, or be pasted into a
 * chat. The rest of this is a partner's switchboard: its address, its website, its
 * concierge desk — published on their own site, and worth the saved typing.
 */
export const partnerPrefillFields = [
  'website',
  'address',
  'contactName',
  'position',
  'contactEmail',
  'phone',
] as const satisfies readonly (keyof PartnerProfileDraftType)[];

export type PartnerPrefillFieldType = (typeof partnerPrefillFields)[number];

/**
 * Prefill payload. `brand` rides along separately because it is not a draft
 * field: like `name`, it is Eighty Days' to set, so the wizard shows it rather
 * than asking.
 */
export type PartnerInvitePrefillType = Partial<Pick<PartnerProfileDraftType, PartnerPrefillFieldType>> & {
  brand?: string;
};

/** Narrows a partner record to what an invitation may reveal about it. */
export function pickPartnerInvitePrefill(partner: PartnerType): PartnerInvitePrefillType {
  return {
    brand: partner.brand,
    website: partner.website,
    address: partner.address,
    contactName: partner.contactName,
    position: partner.position,
    contactEmail: partner.contactEmail,
    phone: partner.phone,
  };
}

/**
 * Folds a prefill into a draft, filling blanks only.
 *
 * Order matters: the prefill arrives after the form has mounted, and possibly
 * after a half-finished session has been restored from storage. Anything already
 * in the field is the manager's, and outranks what we think we know.
 */
export function applyPartnerPrefill(
  draft: PartnerProfileDraftType,
  prefill: PartnerInvitePrefillType,
): PartnerProfileDraftType {
  const merged = { ...draft };
  for (const field of partnerPrefillFields) {
    const value = prefill[field];
    if (value && !merged[field]) merged[field] = value;
  }
  return merged;
}

/** Pulls the editable half out of a partner record, or blanks if there is none. */
export function makePartnerProfileDraft(partner?: PartnerType): PartnerProfileDraftType {
  return {
    website: partner?.website ?? '',
    address: partner?.address ?? '',
    contactName: partner?.contactName ?? '',
    position: partner?.position ?? '',
    contactEmail: partner?.contactEmail ?? '',
    phone: partner?.phone ?? '',
    accountingName: partner?.accountingName ?? '',
    accountingEmail: partner?.accountingEmail ?? '',
    billingAddress: partner?.billingAddress ?? '',
  };
}

/** Required fields still blank in a draft — empty when step 1 may be left. */
export function missingPartnerProfileFields(
  draft: PartnerProfileDraftType,
): (keyof PartnerProfileDraftType)[] {
  return partnerRequiredProfileFields.filter((f) => !draft[f]?.trim());
}

/**
 * Whether this partner's manager may use the site: the record is filled in and
 * the terms have been accepted. Read by `PartnerOnboardingGate` on every load,
 * so it must stay a pure function of the document — no clock, no claims.
 */
export function partnerOnboardingComplete(partner: PartnerType): boolean {
  return !!partner.termsAcceptedAt && missingPartnerProfileFields(makePartnerProfileDraft(partner)).length === 0;
}
